import { Component, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { RouterModule, NavigationEnd, Router } from '@angular/router';
import { SidebarComponent } from '../../shared/sidebar/sidebar.component';
import { SidebarService } from '../../../services/sidebar.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { AppHeaderService } from '../../../services/app-header.service';
import { ChangePasswordDialogComponent } from '../../shared/dialogs/change-password-dialog/change-password-dialog.component';
import { CommercialVisibilityDialogComponent } from '../../shared/dialogs/commercial-visibility-dialog/commercial-visibility-dialog.component';
import { PwaService } from '../../../services/pwa.service';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../services/auth.service';

@Component({ selector: 'app-main-layout', standalone: true, imports: [SidebarComponent, RouterModule, CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, TranslateModule], templateUrl: './main-layout.component.html', styleUrl: './main-layout.component.scss' })
export class MainLayoutComponent implements OnInit {
  @ViewChild('mainScrollContainer') mainScrollContainer?: ElementRef<HTMLElement>;
  private sidebarService = inject(SidebarService);
  private appHeaderService = inject(AppHeaderService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private pwaService = inject(PwaService);
  private authService = inject(AuthService);
  title$ = this.appHeaderService.title$;
  currentUrl = '';
  isInstallable = false;
  isStandalone = false;
  commercialVisibilityAccepted = false;
  showScrollToTop = false;

  get isCommerciale(): boolean { return sessionStorage.getItem('commerciale') === 'true'; }
  get canManageCommercialVisibility(): boolean { return !this.isCommerciale; }
  get userName(): string { return sessionStorage.getItem('username') || sessionStorage.getItem('denominazione') || 'Utente'; }

  ngOnInit(): void {
    this.isStandalone = this.pwaService.isRunningStandalone();
    this.pwaService.isInstallable$.subscribe((installable) => this.isInstallable = installable);
    this.currentUrl = this.router.url;
    this.appHeaderService.setTitle(this.getDefaultTitle(this.currentUrl));
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      this.currentUrl = event.urlAfterRedirects;
      this.appHeaderService.setTitle(this.getDefaultTitle(this.currentUrl));
      this.closeSidebar();
      this.showScrollToTop = false;
    });
  }

  @HostListener('document:keydown.escape') closeSidebar(): void { this.sidebarService.setCollapsedState(true); }
  toggleSidebar(): void { this.sidebarService.toggle(); }
  onMainScroll(event: Event): void { this.showScrollToTop = (event.target as HTMLElement).scrollTop > 120; }
  scrollToTop(): void {
    const container = this.mainScrollContainer?.nativeElement;
    if (!container) return;
    container.scrollTop = 0;
    this.showScrollToTop = false;
  }
  openChangePasswordDialog(): void { this.dialog.open(ChangePasswordDialogComponent, { width: '400px', disableClose: true, autoFocus: false }); }
  loadCommercialVisibility(): void {
    const taxCode = sessionStorage.getItem('impresaCodiceFiscale');
    if (!this.canManageCommercialVisibility || !taxCode) return;
    this.authService.getConditionsTrackingStatus(taxCode, 'visibilitaCommerciale').subscribe({
      next: response => {
        const record = Array.isArray(response) ? response[0] : response;
        this.commercialVisibilityAccepted = typeof record === 'string'
          ? record.trim().length > 0
          : !!record && typeof record === 'object' && Object.keys(record).length > 0 && record.id !== 0 && record.id !== '0';
      },
      error: () => { this.commercialVisibilityAccepted = false; },
    });
  }
  openCommercialVisibilityDialog(): void { this.dialog.open(CommercialVisibilityDialogComponent, { width: '760px', maxWidth: '95vw', autoFocus: false, panelClass: 'soa-revision-dialog' }).afterClosed().subscribe(() => this.loadCommercialVisibility()); }
  installApp(): void { this.pwaService.promptInstall(); }
  logout(): void { sessionStorage.clear(); window.location.reload(); }

  private getDefaultTitle(url: string): string {
    if (url.startsWith('/businesses/contract-documentation/')) return 'Dettaglio documentazione';
    if (url.includes('/sap/')) return 'Dettaglio SAP';
    if (url.startsWith('/businesses/')) return 'Dettaglio azienda';
    if (url.startsWith('/businesses')) return 'Gestione aziende';
    if (url.startsWith('/contracts/detail/')) return 'Dettaglio contratto';
    if (url.startsWith('/contracts')) return 'Gestione contratti';
    return 'SoaTransfer';
  }
}
